import * as React from "react"
import * as d3 from "d3"
import { EXPANDED_CHART_PANEL_WIDTH_PX, fitChartWidth, resolveChartDefaultWidth } from "./chart_size"
import type { TableRowMeanEntry } from "../../layers/categorical_layer_view"

export type SpiderSeriesColor = {
    fill: string
    stroke: string
}

export type TableRowMeanSpiderSeries = {
    id: string
    name: string
    rows: TableRowMeanEntry[]
    color: SpiderSeriesColor
}

export const SPIDER_SERIES_COLORS: SpiderSeriesColor[] = [
    { fill: "rgba(59, 116, 183, 0.28)", stroke: "rgb(59, 116, 183)" },
    { fill: "rgba(204, 82, 82, 0.28)", stroke: "rgb(204, 82, 82)" },
    { fill: "rgba(46, 139, 87, 0.28)", stroke: "rgb(46, 139, 87)" },
    { fill: "rgba(218, 140, 40, 0.28)", stroke: "rgb(218, 140, 40)" },
    { fill: "rgba(128, 90, 160, 0.28)", stroke: "rgb(128, 90, 160)" },
    { fill: "rgba(0, 151, 167, 0.28)", stroke: "rgb(0, 151, 167)" },
    { fill: "rgba(121, 85, 72, 0.28)", stroke: "rgb(121, 85, 72)" },
    { fill: "rgba(96, 125, 139, 0.28)", stroke: "rgb(96, 125, 139)" },
]

const INITIAL_GROW_DURATION_S = 0.8

function formatMeanValue(mean: number): string {
    return Number.isInteger(mean) ? String(mean) : mean.toFixed(2)
}

function estimateTextWidth(text: string, compact?: boolean): number {
    return text.length * (compact ? 5.2 : 6.2)
}

function wrapLabelLines(
    label: string,
    maxWidth: number,
    compact?: boolean,
    maxLines = 2
): string[] {
    if (!label) return [""]
    if (estimateTextWidth(label, compact) <= maxWidth) return [label]

    const words = label.split(/\s+/).filter(Boolean)
    if (words.length > 1) {
        const lines: string[] = []
        let current = ""

        for (let i = 0; i < words.length; i++) {
            const word = words[i]
            const candidate = current ? `${current} ${word}` : word
            if (estimateTextWidth(candidate, compact) <= maxWidth) {
                current = candidate
                continue
            }

            if (current) lines.push(current)
            current = word

            if (lines.length >= maxLines - 1) {
                const rest = words.slice(i).join(" ")
                if (estimateTextWidth(rest, compact) <= maxWidth) {
                    lines.push(rest)
                } else {
                    let truncated = rest
                    while (truncated.length > 1 && estimateTextWidth(`${truncated}…`, compact) > maxWidth) {
                        truncated = truncated.slice(0, -1)
                    }
                    lines.push(`${truncated}…`)
                }
                return lines.slice(0, maxLines)
            }
        }

        if (current) lines.push(current)
        if (lines.length <= maxLines) return lines
    }

    // No spaces / still too long: hard-wrap by character budget.
    const lines: string[] = []
    let remaining = label
    while (remaining && lines.length < maxLines) {
        if (estimateTextWidth(remaining, compact) <= maxWidth) {
            lines.push(remaining)
            break
        }

        let fitLength = 1
        while (
            fitLength < remaining.length
            && estimateTextWidth(remaining.slice(0, fitLength + 1), compact) <= maxWidth
        ) {
            fitLength++
        }

        if (lines.length === maxLines - 1) {
            let truncated = remaining
            while (truncated.length > 1 && estimateTextWidth(`${truncated}…`, compact) > maxWidth) {
                truncated = truncated.slice(0, -1)
            }
            lines.push(`${truncated}…`)
            break
        }

        lines.push(remaining.slice(0, fitLength))
        remaining = remaining.slice(fitLength).trimStart()
    }

    return lines.length > 0 ? lines : [label]
}

function polarToCartesian(cx: number, cy: number, radius: number, angle: number): [number, number] {
    return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)]
}

function meanByLabel(rows: TableRowMeanEntry[]): Map<string, number> {
    const map = new Map<string, number>()
    for (const row of rows) {
        map.set(row.label.trim().toLowerCase(), row.mean)
    }
    return map
}

export interface TableRowMeanSpiderProps {
    series: TableRowMeanSpiderSeries[]
    /** Axis order / labels follow table order. */
    primaryRows: TableRowMeanEntry[]
    axisLabel: string
    svgId: string
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    /** Square chart size in px. Skips panel fitting when set. */
    layoutSize?: number
    animationResetKey: string
    enableInitialGrow?: boolean
}

export const TableRowMeanSpider = ({
    series,
    primaryRows,
    axisLabel,
    svgId,
    compact,
    panelWidth,
    panelHeight,
    layoutSize,
    animationResetKey,
    enableInitialGrow = true,
}: TableRowMeanSpiderProps) => {
    const [animationComplete, setAnimationComplete] = React.useState(!enableInitialGrow)
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)

    const axesRows = primaryRows

    const spiderMinWidth = compact ? 180 : 400
    const chartWidth = layoutSize ?? fitChartWidth(
        compact,
        panelWidth,
        resolveChartDefaultWidth(compact, panelWidth, 200, 400, 560),
        spiderMinWidth,
        panelHeight
    )
    const isExpandedPanel = layoutSize != null || (!compact && (panelWidth ?? 0) >= EXPANDED_CHART_PANEL_WIDTH_PX - 80)
    const chartHeight = layoutSize ?? (compact ? 210 : chartWidth)
    const fontSize = compact ? 8 : 10
    const labelLineHeight = compact ? 10 : 12
    const labelMaxWidth = compact ? 72 : isExpandedPanel ? 120 : 92
    const labelGap = compact ? 12 : 16
    const axisTitleSpace = compact ? 20 : 26
    const outerPad = compact ? 8 : 12

    // Reserve space for wrapped labels around the plot, then size the spider inside.
    const horizontalLabelRoom = labelMaxWidth + labelGap
    const verticalLabelRoom = labelLineHeight * 2 + labelGap
    const plotWidth = Math.max(80, chartWidth - outerPad * 2 - horizontalLabelRoom * 2)
    const plotHeight = Math.max(80, chartHeight - outerPad * 2 - verticalLabelRoom * 2 - axisTitleSpace)
    const maxRadius = Math.max(28, Math.min(plotWidth, plotHeight) / 2)
    const cx = chartWidth / 2
    const cy = outerPad + verticalLabelRoom + maxRadius

    React.useEffect(() => {
        if (!enableInitialGrow) {
            setAnimationComplete(true)
            return
        }
        setAnimationComplete(false)
        const timer = setTimeout(() => setAnimationComplete(true), 40)
        return () => clearTimeout(timer)
    }, [animationResetKey, enableInitialGrow])

    React.useEffect(() => {
        setHoveredIndex(null)
    }, [animationResetKey])

    if (axesRows.length === 0 || series.length === 0) {
        return (
            <div className="table-mean-chart__empty">
                No row data for this table in the selected area.
            </div>
        )
    }

    const seriesMeans = series.map(entry => meanByLabel(entry.rows))
    const allMeans = series.flatMap((entry, seriesIndex) =>
        axesRows.map(axis => seriesMeans[seriesIndex].get(axis.label.trim().toLowerCase()))
            .filter((value): value is number => value !== undefined)
    )
    const minMean = allMeans.length > 0 ? Math.min(...allMeans) : 0
    const maxMean = allMeans.length > 0 ? Math.max(...allMeans) : 1
    const hasNegative = minMean < 0
    const domainMin = hasNegative ? Math.min(minMean, 0) : 0
    const domainMax = Math.max(maxMean, hasNegative ? 0 : 1)
    const radiusScale = d3.scaleLinear()
        .domain([domainMin, domainMax])
        .range([0, maxRadius])
        .nice()

    const n = axesRows.length
    const angleFor = (i: number) => (i / n) * 2 * Math.PI - Math.PI / 2
    const zeroRadius = radiusScale(0)
    const ringValues = radiusScale.ticks(compact ? 3 : 4).filter(tick => tick >= domainMin && tick <= domainMax)

    const axisMeta = axesRows.map((row, i) => {
        const angle = angleFor(i)
        const lines = wrapLabelLines(row.label, labelMaxWidth, compact)
        return { label: row.label, angle, index: i, lines }
    })

    const seriesPolygons = series.map((entry, seriesIndex) => {
        const means = seriesMeans[seriesIndex]
        const points = axisMeta.map(axis => {
            const mean = means.get(axis.label.trim().toLowerCase())
            if (mean === undefined) return null
            const radius = Math.max(0, radiusScale(mean))
            const [x, y] = polarToCartesian(cx, cy, radius, axis.angle)
            return { ...axis, mean, radius, x, y }
        }).filter((point): point is NonNullable<typeof point> => point !== null)

        if (points.length === 0) return null

        const polygonPath = `M ${points.map(p => `${p.x},${p.y}`).join(" L ")} Z`
        return { entry, seriesIndex, points, polygonPath }
    }).filter((item): item is NonNullable<typeof item> => item !== null)

    const rings = ringValues.map((tick, i) => {
        const r = Math.max(0, radiusScale(tick))
        return (
            <g key={`ring-${i}`}>
                <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={tick === 0 && hasNegative ? "#999" : "#e0e0e0"}
                    strokeWidth={tick === 0 && hasNegative ? 1.5 : 1}
                />
                {r > 8 && (
                    <text
                        x={cx + 3}
                        y={cy - r + (compact ? 9 : 11)}
                        fontSize={compact ? 7 : 9}
                        fill="#888"
                    >
                        {formatMeanValue(tick)}
                    </text>
                )}
            </g>
        )
    })

    const axes = axisMeta.map((axis) => {
        const [endX, endY] = polarToCartesian(cx, cy, maxRadius, axis.angle)
        const labelRadius = maxRadius + labelGap
        const [labelX, labelY] = polarToCartesian(cx, cy, labelRadius, axis.angle)
        const cos = Math.cos(axis.angle)
        const sin = Math.sin(axis.angle)
        const textAnchor = Math.abs(cos) < 0.35 ? "middle" : cos > 0 ? "start" : "end"
        // Shift multi-line labels so they grow away from the chart centre.
        const blockHeight = (axis.lines.length - 1) * labelLineHeight
        const yOffset = Math.abs(cos) < 0.35
            ? (sin < 0 ? -blockHeight : 0)
            : -blockHeight / 2
        const hovered = hoveredIndex === axis.index
        const tooltipLines = seriesPolygons.map(({ entry, points }) => {
            const point = points.find(p => p.index === axis.index)
            if (!point) return null
            return `${entry.name}: ${formatMeanValue(point.mean)}`
        }).filter(Boolean)

        return (
            <g key={`axis-${axis.label}`}>
                <line
                    x1={cx}
                    y1={cy}
                    x2={endX}
                    y2={endY}
                    stroke="#e0e0e0"
                    strokeWidth={1}
                />
                <text
                    x={labelX}
                    y={labelY + yOffset}
                    fontSize={fontSize}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    fill={hovered ? "#000" : "#222"}
                    style={{ fontWeight: hovered ? "bold" : "normal", cursor: "default" }}
                    onMouseEnter={() => setHoveredIndex(axis.index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                >
                    <title>{[axis.label, ...tooltipLines].join("\n")}</title>
                    {axis.lines.map((line, lineIndex) => (
                        <tspan
                            key={lineIndex}
                            x={labelX}
                            dy={lineIndex === 0 ? 0 : labelLineHeight}
                        >
                            {line}
                        </tspan>
                    ))}
                </text>
            </g>
        )
    })

    const polygons = seriesPolygons.map(({ entry, seriesIndex, polygonPath, points }) => (
        <g key={`series-${entry.id}`}>
            <path
                d={polygonPath}
                fill={entry.color.fill}
                stroke={entry.color.stroke}
                strokeWidth={compact ? 1.5 : 2}
                strokeLinejoin="round"
                style={{
                    transformOrigin: `${cx}px ${cy}px`,
                    transform: animationComplete ? "scale(1)" : "scale(0)",
                    transition: enableInitialGrow
                        ? `transform ${INITIAL_GROW_DURATION_S}s ease-out ${seriesIndex * 0.08}s`
                        : undefined,
                }}
            />
            {points.map(point => {
                const hovered = hoveredIndex === point.index
                return (
                    <circle
                        key={`${entry.id}-${point.label}`}
                        cx={point.x}
                        cy={point.y}
                        r={hovered ? (compact ? 4 : 5) : (compact ? 2.5 : 3.5)}
                        fill={entry.color.stroke}
                        stroke="#fff"
                        strokeWidth={1}
                        style={{ cursor: "pointer", transition: "r 0.15s ease" }}
                        onMouseEnter={() => setHoveredIndex(point.index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        <title>{`${entry.name} — ${point.label}: ${formatMeanValue(point.mean)}`}</title>
                    </circle>
                )
            })}
        </g>
    ))

    const hoverLabels = hoveredIndex !== null && animationComplete
        ? seriesPolygons.flatMap(({ entry, points }, seriesIndex) => {
            const point = points.find(p => p.index === hoveredIndex)
            if (!point) return []
            const offset = (seriesIndex - (seriesPolygons.length - 1) / 2) * (compact ? 10 : 12)
            return [(
                <text
                    key={`hover-${entry.id}`}
                    x={point.x}
                    y={point.y - (compact ? 8 : 10) + offset}
                    fontSize={compact ? 8 : 10}
                    textAnchor="middle"
                    fill={entry.color.stroke}
                    style={{ pointerEvents: "none", fontWeight: "bold" }}
                >
                    {formatMeanValue(point.mean)}
                </text>
            )]
        })
        : null

    return (
        <svg
            id={svgId}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            width={chartWidth}
            height={chartHeight}
            preserveAspectRatio="xMidYMid meet"
            style={{
                display: "block",
                maxWidth: "100%",
                height: "auto",
                overflow: "visible",
            }}
        >
            {rings}
            {axes}
            {polygons}
            {zeroRadius > 0 && hasNegative && (
                <circle
                    cx={cx}
                    cy={cy}
                    r={zeroRadius}
                    fill="none"
                    stroke="#999"
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                    style={{ pointerEvents: "none" }}
                />
            )}
            {hoverLabels}
            <text
                x={cx}
                y={chartHeight - (compact ? 6 : 8)}
                fontSize={compact ? 10 : 12}
                textAnchor="middle"
                fill="black"
            >
                {axisLabel}
            </text>
        </svg>
    )
}
